import { mapColumnTypes, TYPES } from "@/react/components/table/columns";
import {
  international,
  regionOptions,
  subnet,
} from "@/react/features/globalization/schema";
import { getColumnDef } from "@/react/schema/schemaFields";
import selectColumnFactory from "@/react/components/table/components/selectColumnFactory";
import { ListFilter } from "@/react/components/table/components/filter/filters";
import React from "react";
import DateCell from "@/react/features/globalization/DateCell";
import {
  BooleanCellPrintReadable,
  EditorText,
  LastUpdateFromList,
  PseudoCellRender,
} from "@/react/features/globalization/cellRenderers";
import Button from "@8x8/oxygen-button";
import { GLOBALIZATION_INTERNATIONAL_EDIT_BASE } from "@/react/routing/constants";
import {
  booleanEdit,
  numberEdit,
  optionEdit,
  stringEdit,
  textEdit,
} from "@/react/features/globalization/components/cell-edit-renders";
import {
  HardPhones,
  InboundCloud,
  LNPDetails,
  MeetAudio,
  NumberingDetails,
  Porting,
  ProductSupport,
  PSTNCloud,
  XSeries,
} from "@/react/features/globalization/components/cell-renders";
import expandColumnFactory from "@/react/components/table/components/expandColumnFactory";
import { tooltips } from "@/react/features/globalization/tooltips";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

const StyledEditorText = styled(EditorText)`
  width: 500px;
`;

export const internationalColumnDefinition = [
  { ...selectColumnFactory(), header: null },
  {
    ...expandColumnFactory(),
  },
  {
    ...getColumnDef(international.fields.region, "Region"),
    type: TYPES.SELECT,
    options: regionOptions.reduce(
      (obj, region) => ({ ...obj, [region]: region }),
      {}
    ),
    size: 70,
  },
  {
    ...getColumnDef(international.fields.country, "Country"),
    type: TYPES.ID,
  },
  {
    ...getColumnDef(international.fields.PSTN_cloud, "PSTN Cloud"),
    type: TYPES.BOOLEAN,
    size: 100,
  },
  {
    ...getColumnDef(
      international.fields.inbound_cloud,
      "Inbound Cloud",
      tooltips.InboundCloud
    ),
    type: TYPES.BOOLEAN,
    size: 130,
  },
  {
    ...getColumnDef(
      international.fields.PSTN_gateway_support,
      "PSTN Gateway Required",
      tooltips.PSTNGatewaySupport
    ),
    type: TYPES.BOOLEAN,
    size: 190,
  },
  {
    ...getColumnDef(
      international.fields.phone_number_tier,
      "Phone number tier",
      tooltips.PhoneNumberTier
    ),
    type: TYPES.NUMBER,
    size: 150,
  },
  {
    ...getColumnDef(
      international.fields.PSTN_cloud_carrier,
      "PSTN Cloud Carrier",
      tooltips.PSTNCloudCarrier
    ),
    size: 160,
  },
  {
    id: "options",
    header: () => <span>Options</span>,
    cell: ({ row }) => {
      const navigate = useNavigate();
      return (
        <Button
          size="small"
          onClick={() => {
            navigate(
              `${GLOBALIZATION_INTERNATIONAL_EDIT_BASE}/${row.original.id}`
            );
          }}
        >
          Edit
        </Button>
      );
    },
    size: 70,
    enableHiding: false,
    enableSorting: false,
    enableDownload: false,
    enableResizing: false,
  },
].map(mapColumnTypes);

export const subnetColumnDefinition = [
  {
    ...getColumnDef(subnet.fields.region, "Region"),
    filterFns: new Map([
      [
        "in list",
        {
          label: "In list",
          filterFn: "arrIncludesSome",
          filterComponent: ListFilter,
          displayComponent: (displayFilter) =>
            `${displayFilter.filterFnId} (${displayFilter.value?.length || 0})`,
        },
      ],
    ]),
  },
  {
    ...getColumnDef(subnet.fields.service, "Service"),
  },
  {
    ...getColumnDef(subnet.fields.subnetString, "Subnet"),
  },
  {
    ...getColumnDef(subnet.fields.portUsage, "Port Usage"),
    filterFns: new Map([
      [
        "in list",
        {
          label: "In list",
          filterFn: "arrIncludesSome",
          filterComponent: ListFilter,
          displayComponent: (displayFilter) =>
            `${displayFilter.filterFnId} (${displayFilter.value?.length || 0})`,
        },
      ],
    ]),
  },
  {
    ...getColumnDef(subnet.fields.QOS, "QOS"),
    type: TYPES.BOOLEAN,
    size: 10,
  },
  {
    ...getColumnDef(subnet.fields.ACL, "ACL"),
    type: TYPES.BOOLEAN,
    size: 10,
  },
  {
    ...getColumnDef(subnet.fields.is_proxy, "Proxy"),
    type: TYPES.BOOLEAN,
    defaultHidden: true,
    size: 10,
  },
  {
    ...getColumnDef(subnet.fields.is_non_proxy, "NonProxy"),
    type: TYPES.BOOLEAN,
    defaultHidden: true,
    size: 10,
  },
  {
    ...getColumnDef(subnet.fields.is_data_center, "Data Center"),
    type: TYPES.BOOLEAN,
    defaultHidden: true,
    size: 10,
  },
].map(mapColumnTypes);

export const internationalHorizontal = [
  {
    name: "Region",
    field: (row) => row.region,
    edit: (row, onChange) =>
      optionEdit(
        [
          { value: "APAC", label: "APAC" },
          { value: "EMEA", label: "EMEA" },
          { value: "LATAM", label: "LATAM" },
          { value: "NA", label: "NA" },
        ],
        row,
        "region",
        onChange
      ),
    id: "region",
    meta: { right: "region" },
  },
  {
    name: "Country",
    field: (row) => row.country,
    edit: (row, onChange) => stringEdit(row, "country", onChange),
    id: "country",
    meta: { right: "country" },
  },
  {
    name: "PSTN Cloud",
    render: (row) => <BooleanCellPrintReadable value={row.PSTN_cloud} />,
    edit: (row, onChange) => booleanEdit(row, "PSTN_cloud", onChange),
    id: "PSTN_cloud",
    meta: { right: "PSTN_cloud" },
  },
  {
    name: "PSTN Cloud Carrier",
    field: (row) => row.PSTN_cloud_carrier,
    edit: (row, onChange) => stringEdit(row, "PSTN_cloud_carrier", onChange),
    id: "PSTN_cloud_carrier",
    meta: { right: "PSTN_cloud_carrier" },
    tooltip: tooltips.PSTNCloudCarrier,
  },
  {
    name: "Inbound Cloud",
    tooltip: tooltips.InboundCloud,
    render: (row) => <BooleanCellPrintReadable value={row.inbound_cloud} />,
    edit: (row, onChange) => booleanEdit(row, "inbound_cloud", onChange),
    id: "inbound_cloud",
    meta: { right: "inbound_cloud" },
  },
  {
    name: "PSTN Cloud",
    tooltip: tooltips.PSTN_Cloud,
    render: (row, { onCellChange }) => (
      <PSTNCloud row={row} onChange={onCellChange} />
    ),
    meta: { id: "geographic_PSTN" },
  },
  {
    name: "Inbound Cloud",
    tooltip: tooltips.Inbound_Cloud,
    render: (row, { onCellChange }) => (
      <InboundCloud row={row} onChange={onCellChange} />
    ),
  },
  {
    name: "Porting",
    tooltip: tooltips.Porting,
    render: (row, { onCellChange }) => (
      <Porting row={row} onChange={onCellChange} />
    ),
  },
  {
    name: "8x8 Meet Audio Dial-In Numbers",
    tooltip: tooltips["8x8_Meet_Audio_Dial_In_Numbers"],
    render: (row, { onCellChange }) => (
      <MeetAudio row={row} onChange={onCellChange} />
    ),
  },
  {
    name: "Phone Number Tier",
    render: (row) => row.phone_number_tier.toString(),
    edit: (row, onChange) => numberEdit(row, "phone_number_tier", onChange),
    id: "phone_number_tier",
    tooltip: tooltips.PhoneNumberTier,
    meta: { right: "phone_number_tier" },
  },
  {
    name: "PSTN Gateway Required",
    tooltip: tooltips.PSTNGatewaySupport,
    render: (row) => (
      <BooleanCellPrintReadable value={row.PSTN_gateway_support} />
    ),
    edit: (row, onChange) => booleanEdit(row, "PSTN_gateway_support", onChange),
    meta: { right: "PSTN_gateway_support" },
  },
  {
    name: "Hard Phones",
    render: (row, { onCellChange }) => (
      <HardPhones row={row} onChange={onCellChange} />
    ),
  },
  {
    name: "X-Series Unmetered Availability",
    tooltip: tooltips["X_Series_Country_Calling_Support"],
    render: (row, { onCellChange }) => (
      <XSeries row={row} onChange={onCellChange} />
    ),
  },
  {
    name: "Product Support",
    render: (row, { onCellChange }) => (
      <ProductSupport row={row} onChange={onCellChange} />
    ),
  },
  {
    name: "Licensed",
    tooltip: tooltips.Licensed,
    render: (row) => <BooleanCellPrintReadable value={row.licensed} />,
    edit: (row, onChange) => booleanEdit(row, "licensed", onChange),
    meta: { right: "licensed" },
  },
  {
    name: "Country Specific Caveats",
    tooltip: tooltips.CountrySpecificCaveats,
    render: (row) => (
      <StyledEditorText>
        {row.country_specific_caveats ? row.country_specific_caveats : "N/A"}
      </StyledEditorText>
    ),
    edit: (row, onChange) =>
      textEdit(row, "country_specific_caveats", onChange),
    meta: { right: "country_specific_caveats" },
  },
  {
    name: "Numbering Details",
    render: (row) => <NumberingDetails row={row} />,
  },
  {
    name: "LNP Details",
    render: (row) => <LNPDetails row={row} />,
  },
  {
    name: "Last Update",
    isPrintable: false,
    render: (row) => (
      <>
        <PseudoCellRender
          header="International"
          value={row.last_update ? <DateCell value={row.last_update} /> : "N/A"}
        />
        <LastUpdateFromList title={"LNP Details"} list={row.lnpDetails} />
        <LastUpdateFromList
          title={"International Details"}
          list={row.internationalDetails}
        />
      </>
    ),
  },
];
